import { isAxiosError } from 'axios'
import type { UnknownAction } from 'redux'
import type { ThunkAction } from 'redux-thunk'

import { apiAxios } from '@/api'
import { fetchRecommendedOffers } from '@/actions/settings/billingActions'
import {
  fetchIfNeededWrapper,
  fetchWrapper,
  getFetchError,
  invalidateFetch,
} from '@/reducers/fetch'
import { selectHasAppAccess } from '@/selectors/user.selectors'
import type { Dispatch, GetState, ReduxState } from '@/utils/typeHelpers'
import { logHeardError } from '@/utils/errorHelpers'

import { QUESTIONS } from './questions/registry'
import { selectReport } from './compliance.selectors'
import {
  hydrateAnswers,
  setLicenseOptions,
  setLicenses,
  setRecommendedOffers,
  setReport,
} from './compliance.slice'
import {
  FETCH_INTAKE_KEY,
  FETCH_REPORT_KEY,
  INTAKE_API_PATH,
  REPORT_API_PATH,
} from './lib/constants'
import type {
  ComplianceReport,
  IntakeAnswers,
  IntakeView,
  SubmitIntakeResult,
} from './lib/types'

type ComplianceThunk<R = void> = ThunkAction<
  Promise<R>,
  ReduxState,
  void,
  UnknownAction
>

/** Load the user's existing intake answers + licenses (pre-fills the wizard). */
export const fetchIntake = () =>
  fetchWrapper({
    fetchKey: FETCH_INTAKE_KEY,
    fetchFunction: async (dispatch: Dispatch) => {
      const { data } = await apiAxios.get<IntakeView>(INTAKE_API_PATH)
      dispatch(hydrateAnswers(data.answers ?? {}))
      dispatch(setLicenses(data.licenses ?? []))
      dispatch(setLicenseOptions(data.licenseOptions ?? {}))
    },
  })

/**
 * Every question key this client can speak for: the intake's own set, plus a
 * handful the API accepts that no `QuestionDef` produces.
 *
 * `fetchIntake` hydrates whatever the server has stored for this user, which
 * can include keys from an older version of the intake — a question that has
 * since been renamed or dropped. Sending those back is what the submit
 * endpoint rejects (it validates unknown keys at the request boundary), and it
 * would take the whole submission down over an answer to a question that no
 * longer exists. So the client asserts values only for questions it actually
 * has; anything else stays in Redux and simply isn't submitted.
 */
const SUBMITTABLE_KEYS = new Set<string>([
  ...QUESTIONS.map((q) => q.key),
  // Keys the API accepts that no `QuestionDef` produces: a field signup owns,
  // and five the intake deliberately stopped asking whose stored answers are
  // still worth carrying forward, `top_concerns` among them now that the
  // closer screen is cut, so a user who answered it before doesn't have it
  // dropped on their next submit. Listed rather than derived because the
  // client has no way to read the API's key set. Worth replacing with one the
  // `GET /intake` payload declares, so this can't drift.
  'top_concerns',
  'mobile_phone',
  'has_w2_income',
  'additional_credentials',
  'ceu_tracking_opt_in',
  'profession',
])

type SubmitIntakeOutcome = { ok: true } | { ok: false; status: number | null }

/** Persist a completed intake round. Reports the outcome so the caller can
 *  route on success and report the rejected status on failure. */
export const submitIntake =
  (answers: IntakeAnswers): ComplianceThunk<SubmitIntakeOutcome> =>
  async (dispatch: Dispatch) => {
    try {
      const payload = {
        answers: Object.entries(answers)
          .filter(
            ([questionKey, value]) =>
              value !== undefined && SUBMITTABLE_KEYS.has(questionKey)
          )
          .map(([questionKey, value]) => ({ questionKey, value })),
      }
      const { data } = await apiAxios.post<SubmitIntakeResult>(
        `${INTAKE_API_PATH}/submit`,
        payload
      )
      dispatch(setLicenses(data.licenses ?? []))
      // New answers mean a new report: without this the deduped `fetchReport`
      // would serve the one composed from the previous submission.
      dispatch(invalidateFetch(FETCH_REPORT_KEY))
      return { ok: true }
    } catch (err) {
      logHeardError(err, { context: 'compliance: submit intake' })
      return {
        ok: false,
        status: isAxiosError(err) ? (err.response?.status ?? null) : null,
      }
    }
  }

/** A 4xx or 5xx is deliberately excluded: re-asking costs another narrative generation and
 *  would bury a real failure. */
const connectionDropped = (err: unknown): boolean =>
  isAxiosError(err) && !err.response

const requestReport = () => apiAxios.get<ComplianceReport>(REPORT_API_PATH)

/**
 * Compose the just-in-time report. Slow: the AI narrative runs server-side.
 *
 * `fetchIfNeededWrapper` is what keeps this to one generation per report: each
 * call re-runs the ~20s narrative and can word it differently, so a remount of
 * the report route must not pay for a second one. It also declines while a
 * request is in flight, so two generations can't race.
 */
export const fetchReport = () =>
  fetchIfNeededWrapper({
    fetchKey: FETCH_REPORT_KEY,
    fetchFunction: async (dispatch: Dispatch) => {
      const { data } = await requestReport().catch((err) => {
        if (!connectionDropped(err)) throw err
        return requestReport()
      })
      dispatch(setReport(data))
    },
  })

/** The reader asking again: invalidates unconditionally, so it regenerates even over a
 *  report already on screen. */
export const retryReport =
  (): ComplianceThunk => async (dispatch: Dispatch) => {
    dispatch(invalidateFetch(FETCH_REPORT_KEY))
    await dispatch(fetchReport())
  }

/** Entry point for the route and its resume listeners. Clears a settled error so
 *  `fetchReport` re-asks, but leaves a report already on screen alone. */
export const loadReport =
  (): ComplianceThunk => async (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    if (getFetchError(state, FETCH_REPORT_KEY) && !selectReport(state)) {
      dispatch(invalidateFetch(FETCH_REPORT_KEY))
    }
    await dispatch(fetchReport())
  }

/** A resume-driven re-ask. `statusCode` is absent exactly when no response arrived, which
 *  is the only failure worth another generation. Reports whether it asked, so a caller can
 *  bound how often it does. */
export const recoverReport =
  (): ComplianceThunk<boolean> => async (dispatch: Dispatch, getState) => {
    const state = getState()
    const error = getFetchError(state, FETCH_REPORT_KEY)
    if (!error || error.statusCode !== undefined || selectReport(state)) {
      return false
    }
    dispatch(invalidateFetch(FETCH_REPORT_KEY))
    await dispatch(fetchReport())
    return true
  }

/** Load the report's plan recommendations into the store. */
export const fetchReportOffers =
  (): ComplianceThunk => async (dispatch: Dispatch, getState: GetState) => {
    // A viewer with app access gets no closing card, so nothing would read the result —
    // and the endpoint 500s for anyone without a financial profile. Only a settled `true`
    // withholds it, matching the rule the card's placement hook uses.
    if (selectHasAppAccess(getState()) === true) return

    const result = await dispatch(fetchRecommendedOffers({ context: 'report' }))
    if (result) dispatch(setRecommendedOffers(result))
  }
